import useSWR, { SWRConfiguration } from 'swr';
import type { TrendingData } from '@/components/predic/ui/type';
import { useMemo } from 'react';
import useUserPnl from './use-user-pnl';

export interface TrendingSearchResponse {
  success: boolean;
  data: TrendingData[];
  message?: string;
  error?: string;
}

export interface UseTrendingSearchOptions {
  enabled?: boolean;
  swr?: SWRConfiguration<TrendingSearchResponse, Error>;
}

export default function useTrendingSearch(options: UseTrendingSearchOptions = {}) {
  const { enabled = true, swr } = options;
  const swrKey = enabled ? 'api:/trending-search' : null;
  const { data: apiAccessToken } = useSWR('apiAccessToken');
  const shouldLoadUserPnl = Boolean(apiAccessToken);

  const { currentValue, pnlPercent } = useUserPnl({
    enabled: shouldLoadUserPnl,
    swr: {
      revalidateOnFocus: true,
    },
  });

  const { data, error, isLoading, isValidating, mutate } = useSWR<TrendingSearchResponse, Error>(
    swrKey,
    swr,
  );

  const list = useMemo(() => {
    const rawList = Array.isArray(data?.data) ? data.data : [];

    return rawList.map((item) => {
      if (item.type !== 'portfolio' || !item.portfolio) {
        return item;
      }

      return {
        ...item,
        portfolio: {
          ...item.portfolio,
          currentValue,
          pnlPercent,
          totalValue: currentValue,
          change24h: {
            ...item.portfolio.change24h,
            percentage: pnlPercent,
          },
        },
      };
    });
  }, [currentValue, data?.data, pnlPercent]);

  return {
    data,
    list,
    error,
    isLoading: isLoading,
    isValidating,
    refresh: mutate,
  };
}
